import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import { FantasyEvents } from '@app/client/common/structs/events/events';
import { registerEvent } from '@app/store/actions/events';
import { useFantasyMetadataStore } from '@app/store/zustand';

export const useFantasyTracking = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const sharedProps = {
        utm_campaign: queryParams.get('utm_campaign') || null,
        utm_source: queryParams.get('utm_source') || null,
        utm_medium: queryParams.get('utm_medium') || null,
    };

    const { currentGame, currentGameWishlist } = useFantasyMetadataStore();

    const game_id = currentGame?.id || '';
    const game_slug = currentGame?.slug || '';

    // utils ------------

    const getStatus = () => {
        const now = dayjs().utc();

        const submissionsClosed =
            now.isBefore(currentGame?.submission_starts_at) ||
            (now.isAfter(currentGame?.submission_ends_at) && !currentGameWishlist);

        let game_status = 'live';
        if (now.isBefore(currentGame?.submission_starts_at)) game_status = 'upcoming';
        if (now.isAfter(currentGame?.ends_at)) game_status = 'completed';

        const lineup_state = submissionsClosed || currentGameWishlist ? 'locked' : 'editable';

        return { game_status, lineup_state };
    };

    // fantasy_prod_lineup_viewed ------------

    const trackLineupViewed = () => {
        const { game_status, lineup_state } = getStatus();
        registerEvent(FantasyEvents.fantasy_prod_lineup_viewed, {
            ...sharedProps,
            game_id,
            game_slug,
            game_status,
            lineup_state,
        });
    };

    // fantasy_prod_lineup_started ------------

    const trackLineupStarted = () => {
        registerEvent(FantasyEvents.fantasy_prod_lineup_started, {
            ...sharedProps,
            game_id,
            game_slug,
        });
    };

    // fantasy_prod_lineup_generate_clicked ------------

    const trackLineupGenerateClicked = (collection_slug: string, quantity: number) => {
        registerEvent(FantasyEvents.fantasy_prod_lineup_generate_clicked, {
            ...sharedProps,
            collection_slug,
            game_id,
            game_slug,
            quantity,
        });
    };

    // fantasy_prod_lineup_colln_searched ------------

    const trackLineupCollectionSearched = (collection_slug: string, search_query: string) => {
        registerEvent(FantasyEvents.fantasy_prod_lineup_colln_searched, {
            ...sharedProps,
            game_id,
            game_slug,
            collection_slug,
            search_query,
        });
    };

    // fantasy_prod_collection_details_view ------------

    type CollectionClickThroughSource = 'lineup' | 'leaderboard' | 'search';

    const trackCollectionDetailsView = (collection_slug: string, source: CollectionClickThroughSource) => {
        registerEvent(FantasyEvents.fantasy_prod_collection_details_view, {
            ...sharedProps,
            game_id,
            game_slug,
            collection_slug,
            source,
        });
    };

    // fantasy_prod_lineup_submitted ------------

    const trackLineupSubmitted = () => {
        const { game_status } = getStatus();
        registerEvent(FantasyEvents.fantasy_prod_lineup_submitted, {
            ...sharedProps,
            game_id,
            game_slug,
            game_status,
        });
    };

    // fantasy_prod_lineup_named ------------

    const trackLineupNamed = (name: string) => {
        registerEvent(FantasyEvents.fantasy_prod_lineup_named, {
            ...sharedProps,
            game_id,
            game_slug,
            name,
        });
    };

    // fantasy_prod_leaderboard_open ------------

    const trackLeaderboardOpen = () => {
        registerEvent(FantasyEvents.fantasy_prod_leaderboard_open, {
            ...sharedProps,
            game_id,
            game_slug,
        });
    };

    // nav_fantasy_click ------------

    type PageClickThroughSource =
        | 'all-games-card'
        | 'create-lineup-button'
        | 'leaderboard-button'
        | 'all-games-card-alpha'
        | 'fantasy-landing-play-button';

    const trackNavFantasyClick = (game_id: string, game_slug: string, source: PageClickThroughSource) => {
        registerEvent(FantasyEvents.nav_fantasy_click, {
            ...sharedProps,
            game_id,
            game_slug,
            source,
        });
    };

    // fantasy_prod_profile_view ------------

    type ProfileClickThroughSource = 'leaderboard' | 'howitworks';

    const trackProfileView = (yat: string, source: ProfileClickThroughSource) => {
        registerEvent(FantasyEvents.fantasy_prod_profile_view, {
            ...sharedProps,
            game_id,
            game_slug,
            yat,
            source,
        });
    };

    // Alpha tracking
    const trackFantasyAlphaLatestGame = (game_id: string) => {
        registerEvent(FantasyEvents.fantasy_alpha_get_latest_game, {
            ...sharedProps,
            game_id,
        });
    };
    const trackFantasyAlphaPlayClick = (game_id: string) => {
        registerEvent(FantasyEvents.fantasy_alpha_play_click, {
            ...sharedProps,
            game_id,
        });
    };

    return {
        getStatus,
        trackLineupViewed,
        trackLineupStarted,
        trackLineupSubmitted,
        trackLineupGenerateClicked,
        trackLineupCollectionSearched,
        trackLineupNamed,
        trackNavFantasyClick,
        trackLeaderboardOpen,
        trackProfileView,
        trackCollectionDetailsView,
        trackFantasyAlphaLatestGame,
        trackFantasyAlphaPlayClick,
    };
};
